import { useState, useRef, useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useReactiveVar } from '@apollo/client/react';
import { Box, FormGroup, Typography } from '@mui/material';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';

import Modal from '@brainpool/components/molecules/Modal/Modal';
import { OrganizationProps, reactiveVars } from '@brainpool/apollo-connect';
import { loggerService } from '@brainpool/logger';

import UppyUpload from '../../Uppy-V2/Uppy-V2';
import { DocumentSource } from '../../../types/Documents';
import { ContextProps } from '../../../apollo/context';
import { ModalProps } from '../../../types/ModalTypes';
import ModalButtons from '../../ModalButtons/ModalButtons';
import { WebsocketService } from '../../../services/websocketService';
import { useCheckUsageLimit } from '../../../hooks/useCheckUsageLimit';
import { WorkflowStatus } from '../../../types/WorkflowState';
import { Case } from '../../../types/Case';
import { LIST_CASES } from '../../../queries/ListCases';

export function DueDiligenceUploadModalContent({ open = false, handleClose, data }: ModalProps) {
  const apolloClient = useApolloClient();

  const { checkUsageLimit, result } = useCheckUsageLimit();

  const companionEndpoint = useReactiveVar<ContextProps['companionEndpoint']>(
    reactiveVars.companionEndpoint
  ) as string;
  const token = useReactiveVar<string>(reactiveVars.token);
  const organization = useReactiveVar<OrganizationProps>(reactiveVars.organization);

  //state
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [docsUploadingComplete, setDocsUploadingComplete] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const { id } = data?.[0] ?? {};
  const uppyRef = useRef<{
    startCoordinatedUpload: (caseId: string, orgId: string) => void;
  } | null>(null);

  useEffect(() => {
    checkUsageLimit({
      operation: 'createPipelineRun',
      pipelineType: 'duedil',
    });
  }, []);

  useEffect(() => {
    if (docsUploadingComplete) {
      handleUploadSuccess();
    }
  }, [docsUploadingComplete]);

  if (!data || !id) return null;

  const handleFilesChange = (files: File[]) => {
    setFiles(files);
  };

  const handleDocUploadSuccess = () => {
    setDocsUploadingComplete(true);
  };

  const handleUploadSuccess = () => {
    // Optimistically update the case in listCases cache
    const cachedData = apolloClient.cache.readQuery<{ listCases: { items: Case[] } }>({
      query: LIST_CASES,
    });

    if (cachedData?.listCases?.items) {
      const updatedItems = cachedData.listCases.items.map(caseItem => {
        if (caseItem.id === id) {
          return {
            ...caseItem,
            workflow_state: {
              ...caseItem.workflow_state,
              status: WorkflowStatus.DUEDILLIGENCE_STARTED,
              updated_at: new Date().toISOString(),
            },
          };
        }
        return caseItem;
      });

      apolloClient.cache.writeQuery({
        query: LIST_CASES,
        data: {
          listCases: {
            ...cachedData.listCases,
            items: updatedItems,
          },
        },
      });
    }

    const wsService = WebsocketService.getInstance();
    wsService.subscribeToCaseUpdates(id);
    const connectionStatus = wsService.getConnectionStatus(id);
    loggerService.log({
      logLevel: 'info',
      message: 'WebSocket connection status',
      context: {
        caseId: id,
        connectionStatus,
      },
    });
    setDocumentsUploading(false);
    handleClose?.();
  };

  const handleSubmit = () => {
    setDocumentsUploading(true);
    // Start the coordinated upload in the next tick
    if (files.length > 0 && id && organization?.id) {
      // Use Promise.resolve().then() for better timing
      Promise.resolve().then(() => {
        uppyRef.current?.startCoordinatedUpload(id, organization.id);
      });
    } else {
      // No files to upload, redirect immediately
      handleUploadSuccess();
    }
  };

  return (
    <Modal
      titleType="h3"
      title="Due Diligence"
      open={open}
      onClose={handleClose ?? undefined}
      actions={
        <ModalButtons
          onCancel={handleClose ?? undefined}
          onConfirm={handleSubmit}
          isValid={files.length > 0 && result?.allowed}
          isLoading={documentsUploading}
        />
      }
    >
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: '0 !important',
        }}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          In order to produce the full due diligence analysis, we need a full data room. Please
          upload all relevant documents below to start the process.
        </Typography>
        <Typography
          variant="h6"
          component="h2"
          sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
        >
          <UploadRoundedIcon fontSize="medium" sx={{ mr: 1, p: '0.125rem' }} />
          Upload data room documents (pdf, word, pptx and txt documents only. Max 1000 files. Please
          note xsls and csv file are not accepted, financial analysis is coming soon.)
        </Typography>
        <FormGroup>
          <UppyUpload
            pipelineType="duedil"
            ref={uppyRef}
            companionEndpoint={companionEndpoint}
            authToken={token}
            onFilesChange={handleFilesChange}
            onUploadSuccess={handleDocUploadSuccess}
            documentSource={DocumentSource.COMPANY}
            disabled={result?.allowed === false}
          />
        </FormGroup>
      </Box>
    </Modal>
  );
}
